import { useState, useRef, useEffect, useCallback } from "react";

import { Button, Tooltip } from "@mui/material";

import refreshIcon from "@user/assets/images/common/refresh.svg";
import chevronRightIcon from "@user/assets/images/common/chevron-right.svg";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";

import "./main.scoped.scss";

interface State {
}

const defState: State = {
};

interface SearchParams {
}

const defSearchParams: SearchParams = {
};

interface Result {
  forceCompleteInfos: any[],
  tempWorkInfos: any[],
  erpIfLogInfos: any[]
}

const defResult: Result = {
  forceCompleteInfos: [],
  tempWorkInfos: [],
  erpIfLogInfos: []
};

export default function Main(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const userName = useAuthStore((state) => state.userName);
  const location = useTabsStore((state) => state.getLocation(props.id));

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await doSearch();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
  }

  async function doSearch() {
    const searchParams = searchParamsRef.current;

    try {
      const data = await kutil.sendGet("/api/common/main.data?mode=search", {}, {});
      setResult({
        forceCompleteInfos: data.forceCompleteInfos,
        tempWorkInfos: data.tempWorkInfos,
        erpIfLogInfos: data.erpIfLogInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      <div className="main-contents">
        <div>
          <span>
            K-WMS
            <em>KOREAN AIR - Warehouse Management System</em>
          </span>
          <em></em>
        </div>
        {/* 상단 정보 */}
        <div>
          <p>
            <em>{ userName }</em>
            님 안녕하세요.
          </p>
          <Tooltip title="새로고침">
            <button className="icon-button bd" onClick={doSearch}><img src={refreshIcon} alt="새로고침" /></button>
          </Tooltip>
        </div>
        {/*// 상단 정보 */}
        {/* 하단 정보 */}
        <div>
          {/* 강제완료 I/F */}
          <div>
            <p>
              강제완료 I/F
              <Tooltip title="더보기">
                <button className="icon-button" onClick={() => router.navigateByPath("/inoutbound/force_complete_list")}>
                  <img src={chevronRightIcon} alt=" 더보기" />
                </button>
              </Tooltip>
            </p>
            <div className="table-wrap">
              <table className="tbl-list">
                <colgroup>
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">작업구분</th>
                    <th scope="col">상세구분</th>
                    <th scope="col">P/N</th>
                    <th scope="col">작업일</th>
                  </tr>
                </thead>
                <tbody>
                {result.forceCompleteInfos.map((info, idx) => (
                  <tr key={idx}>
                    <td>{ commonCode.getCodeValue("inoutType", info.inoutType) }</td>
                    <td>{ commonCode.getCodeValue("inoutDetailType", info.inoutDetailType) }</td>
                    <td>{ info.pn}</td>
                    <td>{ kutil.date2string(info.workCompleteDate, "yyyy-MM-dd") }</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>
          {/*// 강제완료 I/F */}
          {/* 임시작업 매칭 */}
          <div>
            <p>
              임시작업 매칭
              <Tooltip title="더보기">
                <button className="icon-button" onClick={() => router.navigateByPath("/inoutbound/temp_work_list")}>
                  <img src={chevronRightIcon} alt=" 더보기" />
                </button>
              </Tooltip>
            </p>
            <div className="table-wrap">
              <table className="tbl-list">
                <colgroup>
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "25%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">P/N</th>
                    <th scope="col">TR No</th>
                    <th scope="col">S/N</th>
                    <th scope="col">작업일</th>
                  </tr>
                </thead>
                <tbody>
                {result.tempWorkInfos.map((info, idx) => (
                  <tr key={idx}>
                    <td>{ info.pn}</td>
                    <td>{ info.trNo}</td>
                    <td>{ info.sn}</td>
                    <td>{ kutil.date2string(info.workCompleteDate, "yyyy-MM-dd") }</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>
          {/*// 임시작업 매칭 */}
          {/* ERP I/F 오류 */}
          <div>
            <p>
              ERP I/F 오류
              <Tooltip title="더보기">
                <button className="icon-button" onClick={() => router.navigateByPath("/log/erp_if_log_list")}>
                  <img src={chevronRightIcon} alt=" 더보기" />
                </button>
              </Tooltip>
            </p>
            <div className="table-wrap">
              <table className="tbl-list">
                <colgroup>
                  <col style={{ width: "70%" }} />
                  <col style={{ width: "30%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col">오류</th>
                    <th scope="col">발생일</th>
                  </tr>
                </thead>
                <tbody>
                {result.erpIfLogInfos.map((info, idx) => (
                  <tr key={idx}>
                    <td className="ta-left">{ info.errorContent}</td>
                    <td>{ kutil.date2string(info.ifDate, "yyyy-MM-dd") }</td>
                  </tr>
                ))}
                </tbody>
              </table>
            </div>
          </div>
          {/*// ERP I/F 오류 */}
          {/* 작업 바로가기 */}
          <div>
            <p>작업 바로가기</p>
            <div>
              <Button variant="outlined" onClick={() => router.navigateByPath("/inoutbound/inout_work")}>입출고 작업</Button>
              <Button variant="outlined" onClick={() => router.navigateByPath("/inoutbound/workstation_manage")}>작업대</Button>
              <Button variant="outlined" onClick={() => router.navigateByPath("/inoutbound/backup_work")}>BackUp작업</Button>
              <Button variant="outlined" onClick={() => router.navigateByPath("/report/inout_history")}>입출고 실적</Button>
              <Button variant="outlined" onClick={() => router.navigateByPath("/stock/stock_status")}>재고현황</Button>
            </div>
          </div>
          {/*// 작업 바로가기 */}
        </div>
      </div>
    </>
  );
}
